import { EntityState, createEntityAdapter, createSelector } from '@reduxjs/toolkit';
import { customerApi } from '../api';
import {
  CasStreamingSocketClient,
  AssetLiveDataUpdate,
  SiteLiveDataUpdate,
} from './casStreamingSocketClient';

// Create a singleton instance
const getUrl = window.location;
const baseUrl = (getUrl.protocol === 'https:' ? 'wss://' : 'ws://') + getUrl.host;

const wsUrl = `${baseUrl}/api/ws/assets`;
console.debug('Using WS URL: ', wsUrl);

export const globalStreamingClient: CasStreamingSocketClient = new CasStreamingSocketClient(wsUrl);

export interface StreamState {
  connected: boolean;
}

const liveAssetsAdapter = createEntityAdapter<AssetLiveDataUpdate>({
  sortComparer: (a, b) => (a.n ?? '').localeCompare(b.n ?? ''),
});

const initialState = liveAssetsAdapter.getInitialState({
  ids: [],
  entities: {},
});

export const streamingApi = customerApi.injectEndpoints({
  endpoints: build => ({
    getConnState: build.query<StreamState, void>({
      queryFn: (arg, queryApi, extraOptions, baseQuery) => ({
        data: { connected: false },
      }),
      async onCacheEntryAdded(_, { updateCachedData, cacheDataLoaded, cacheEntryRemoved }) {
        try {
          // wait for the initial query to resolve before proceeding
          await cacheDataLoaded;

          // Initial state
          updateCachedData(draft => {
            draft.connected = globalStreamingClient.getConnState();
          });

          globalStreamingClient.onConnState = (evt: boolean) => {
            updateCachedData(draft => {
              draft.connected = evt;
            });
          };
        } catch {
          // no-op in case `cacheEntryRemoved` resolves before `cacheDataLoaded`, in which case `cacheDataLoaded` will throw
        }

        await cacheEntryRemoved;

        // Cleanup
        globalStreamingClient.onConnState = null;
      },
    }),
    getLiveAssets: build.query<EntityState<AssetLiveDataUpdate, number>, void>({
      queryFn: (arg, queryApi, extraOptions, baseQuery) => ({
        data: initialState,
      }),
      async onCacheEntryAdded(_, { updateCachedData, cacheDataLoaded, cacheEntryRemoved }) {
        try {
          // wait for the initial query to resolve before proceeding
          await cacheDataLoaded;

          globalStreamingClient.onAssetUpdates = (evt: SiteLiveDataUpdate) => {
            updateCachedData(draft => {
              if (evt) {
                if (evt.r) {
                  // Clear everything out and populate with initial values
                  liveAssetsAdapter.setAll(draft, evt.a);
                } else {
                  liveAssetsAdapter.upsertMany(draft, evt.a);
                }
              }
            });
          };

          globalStreamingClient.setAssetInterest(true);
        } catch {
          // no-op in case `cacheEntryRemoved` resolves before `cacheDataLoaded`, in which case `cacheDataLoaded` will throw
        }

        // cacheEntryRemoved will resolve when the cache subscription is no longer active
        await cacheEntryRemoved;

        // perform cleanup steps once the `cacheEntryRemoved` promise resolves
        globalStreamingClient.setAssetInterest(false);
        globalStreamingClient.onAssetUpdates = null;
      },
    }),
  }),
  overrideExisting: false,
});

// Mangle the selectors for query API semantics. See https://stackoverflow.com/questions/76212082/how-to-use-rtk-query-in-combination-with-selectors
const selectEntryResult = createSelector(
  [state => state, (_, params) => params],
  (state, params) =>
    streamingApi.endpoints.getLiveAssets.select(params)(state)?.data ?? initialState,
);

export const liveAssetsSelectors = liveAssetsAdapter.getSelectors(
  state => selectEntryResult(state, undefined) ?? initialState,
);

export const { useGetLiveAssetsQuery, useGetConnStateQuery } = streamingApi;
